// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyNavTab, TyNavTabs } from "./types";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The single source of truth for the dashboard's destinations, already filtered to what this client
 * may open. The sidebar and the mobile tabs bar both read it so a permission change can never show a
 * tab on one surface and hide it on the other.
 */
export default function useNavTabs(): TyNavTabs {
    const t = useTranslation();
    const client = useAuth((state) => state.client);
    const storeId = useStore((state) => state.storeId);

    const isOwner = storeId ? (client?.owner?.includes(storeId) ?? false) : false;

    const hasModel = (model: string) => {
        if (isOwner) return true;
        const access = (client?.storeAccess ?? []).find((a) => a.store === storeId);
        return access?.permissions?.some((p) => p.model === model) ?? false;
    };

    const mainTabs: TyNavTab[] = [
        { path: "/", label: t.sideBar.home, icon: "home", show: true },
        { path: "/menu", label: t.sideBar.menu, icon: "menu", show: hasModel("menu") },
        { path: "/branches", label: t.sideBar.branches, icon: "branches", show: hasModel("branches") },
        { path: "/orders", label: t.sideBar.orders, icon: "orders", show: hasModel("orders") },
        { path: "/clients", label: t.sideBar.clients, icon: "clients", show: hasModel("clients") },
        { path: "/subscriptions", label: t.sideBar.subscriptions, icon: "subscription", show: isOwner },
    ]
        .filter((tab) => tab.show)
        .map(({ path, label, icon }) => ({ path, label, icon }));

    // Settings now holds store details only — the personal half moved to the profile screen — so it
    // is gated like every other tab. Support is reachable by anyone, whatever their access.
    const bottomTabs: TyNavTab[] = [
        { path: "/settings", label: t.sideBar.settings, icon: "settings", show: hasModel("settings") },
        { path: "/support", label: t.sideBar.support, icon: "support", show: true },
    ]
        .filter((tab) => tab.show)
        .map(({ path, label, icon }) => ({ path, label, icon }));

    return { mainTabs, bottomTabs, allTabs: [...mainTabs, ...bottomTabs] };
}
